import { z } from 'zod';
import type { AddFields } from '../../ontology/add-fields.ts';
import { INSTRUMENT_KINDS, MARKETS } from '../../ontology/instrument.ts';
import { idSchema, TRADE_TYPES } from '../../ontology/primitives.ts';

/** Null files as the viewer's own analyst; the desk must name one. */
const addForSchema = z.object({ analystId: idSchema.nullable().default(null) });

/** Strings: `8%` is a target, for the parser to read. */
export const addFieldsSchema = z.object({
  symbol: z.string().trim().min(1).max(40),
  side: z.enum(['long', 'short']),
  entry: z.string().trim().min(1).max(24),
  target: z.string().trim().min(1).max(24),
  stoploss: z.string().trim().min(1).max(24),
  /** A future's month or `spot`; blank is the current month. */
  month: z.string().trim().max(16).optional(),
  strike: z.string().trim().max(24).optional(),
  optionType: z.string().trim().max(4).optional(),
  expiry: z.string().trim().max(16).optional(),
  externalId: z.string().trim().max(32).optional(),
  book: z.string().trim().max(32).optional(),
  date: z.string().trim().max(16).optional(),
}) satisfies z.ZodType<AddFields>;

export type AddFieldsBody = z.infer<typeof addFieldsSchema>;

export const addCheckRequestSchema = addFieldsSchema.extend(addForSchema.shape);

export type AddCheckRequest = z.infer<typeof addCheckRequestSchema>;

const symbolMatchSchema = z.object({
  name: z.string(),
  underlying: z.string(),
  market: z.enum(MARKETS),
  exchange: z.string(),
  tradingsymbol: z.string(),
  kind: z.enum(INSTRUMENT_KINDS),
  expiry: z.string().nullable(),
  lotSize: z.number().int(),
});

export type SymbolMatch = z.infer<typeof symbolMatchSchema>;

export const symbolsReplySchema = z.object({ symbols: z.array(symbolMatchSchema) });

export type SymbolsReply = z.infer<typeof symbolsReplySchema>;

const marketLevelsSchema = z.object({
  tradingsymbol: z.string(),
  exchange: z.string(),
  last: z.number().nullable(),
  day: z.object({ high: z.number(), low: z.number() }).nullable(),
  ranges: z.array(z.object({ label: z.string(), high: z.number(), low: z.number() })),
});

export type MarketLevels = z.infer<typeof marketLevelsSchema>;

export const levelsReplySchema = z.object({ levels: marketLevelsSchema.nullable() });

export type LevelsReply = z.infer<typeof levelsReplySchema>;

export const addCheckReplySchema = z.union([
  z.object({
    ok: z.literal(true),
    underlying: z.string(),
    market: z.enum(MARKETS),
    instrument: z.object({
      tradingsymbol: z.string(),
      exchange: z.string(),
      kind: z.enum(INSTRUMENT_KINDS),
      expiry: z.string().nullable(),
      lotSize: z.number().int(),
    }),
    tradeType: z.enum(TRADE_TYPES),
    entryPrice: z.number(),
    targetPrice: z.number(),
    stoplossPrice: z.number(),
    /** The expiring month the levels were given on, and how far they were moved off it. */
    drawn: z.object({ tradingsymbol: z.string(), basis: z.number() }).nullable(),
    book: z.string(),
    newBook: z.boolean(),
  }),
  z.object({
    ok: z.literal(false),
    violations: z.array(z.string()).min(1),
    hint: z.string().nullable(),
  }),
]);

export type AddCheckReply = z.infer<typeof addCheckReplySchema>;
